import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useCallback } from "react";
import { Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useApi } from "@/hooks/use-api";
const DISABLED_STATUSES = [
    "new",
    "investigating",
    "issue_created",
    "fixing",
    "deploying",
    "fix_failed",
    "approved",
    "merged",
    "closed",
    "wont_fix",
    "duplicate",
];
const PLACEHOLDER_MAP = {
    new: "Agent is being assigned to your ticket...",
    investigating: "Agent is investigating your ticket...",
    awaiting_reporter: "Type your response to the agent...",
    issue_created: "Issue has been created on GitHub...",
    fixing: "Agent is working on a fix...",
    staging_ready: "Ticket is ready for testing. Add your review notes...",
    deploying: "Deploying to staging environment...",
    testing: "Testing on staging. Add your feedback...",
    approved: "Ticket has been approved.",
    merged: "Fix has been merged. Ticket is complete.",
    closed: "Ticket is closed.",
};
export function ChatInput({ ticketId, ticketStatus }) {
    const [message, setMessage] = useState("");
    const [sending, setSending] = useState(false);
    const { callApi } = useApi();
    const isDisabled = DISABLED_STATUSES.includes(ticketStatus);
    const placeholder = PLACEHOLDER_MAP[ticketStatus] ?? "Type a message...";
    const handleSend = useCallback(async () => {
        const trimmed = message.trim();
        if (!trimmed || sending || isDisabled)
            return;
        try {
            setSending(true);
            await callApi("ticket.reply", {
                ticketId,
                message: trimmed,
            });
            setMessage("");
        }
        catch (err) {
            console.error("Failed to send message:", err);
        }
        finally {
            setSending(false);
        }
    }, [message, sending, isDisabled, ticketId, callApi]);
    const handleKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };
    return (_jsxs("div", { className: "border-t bg-background p-4", children: [_jsxs("div", { className: "flex items-end gap-2", children: [_jsx(Textarea, { value: message, onChange: (e) => setMessage(e.target.value), onKeyDown: handleKeyDown, placeholder: placeholder, disabled: isDisabled || sending, rows: 1, className: "max-h-32 min-h-[40px] resize-none" }), _jsx(Button, { onClick: handleSend, disabled: isDisabled || sending || !message.trim(), size: "icon", className: "h-10 w-10 shrink-0", children: sending ? (_jsx("span", { className: "h-4 w-4 animate-spin rounded-full border-2 border-primary-foreground border-t-transparent" })) : (_jsx(Send, { className: "h-4 w-4" })) })] }), isDisabled && ticketStatus !== "awaiting_reporter" && (_jsx("p", { className: "mt-1.5 text-xs text-muted-foreground", children: placeholder }))] }));
}
